import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { VFC } from 'react';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { SelectOptionValue } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { VERIFICATION_VALUES_OPTIONS } from 'dashboard/lib/constants/verification-filters';
import { DynamicFilterSet } from 'dashboard/models/metrics-verification';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';

const Grid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 31% 28% 33%;
  column-gap: ${Spacing[2]};
`;

const singleSelectStyles = {
  control: () => ({
    height: `${Spacing[6]}`,
  }),
  container: () => ({
    minHeight: `${Spacing[6]}`,
  }),
  valueContainer: () => ({
    height: `${Spacing[6]}`,
  }),
  indicatorsContainer: () => ({
    height: `${Spacing[6]}`,
  }),
};

const InnerGrid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 70% 30%;
`;

const CenteredIcon = styled.div`
  display: block;
  margin-top: auto;
  margin-bottom: auto;
`;

const StyledLabel = styled(Label)`
  padding: ${Spacing[2]} 0;
`;

const StyledSelect = styled(ReactSelect<FilterDimensions>)`
  position: relative;
  margin-right: ${Spacing[2]};
`;

const OPERATOR_TYPES: SelectOptionValue<string>[] = [
  {
    label: (
      <>
        Contains <HelpIcon>The dimension is in one of the following values</HelpIcon>
      </>
    ),
    value: 'IN',
  },
  {
    label: (
      <>
        Does not contain <HelpIcon>The dimension excludes all of the following values</HelpIcon>
      </>
    ),
    value: 'NOT IN',
  },
];

const FIELD_OPTIONS: Record<FilteredVerificationType, OptionType<string>[]> = {
  [ApiModel.VerificationGovernmentId]: [
    {
      value: 'capture_method',
      label: (
        <>
          Capture method <HelpIcon>The method used to submit the ID</HelpIcon>
        </>
      ),
    },
    {
      value: 'device_group',
      label: (
        <>
          Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
        </>
      ),
    },
    {
      value: 'id_type',
      label: (
        <>
          ID class <HelpIcon>The detected type of Identification Document</HelpIcon>
        </>
      ),
    },
    {
      value: 'issuing_country',
      label: (
        <>
          Issuing country <HelpIcon>The detected country that issued the ID</HelpIcon>
        </>
      ),
    },
    {
      value: 'issuing_subdivision',
      label: (
        <>
          Issuing subdivision{' '}
          <HelpIcon>The detected state, province, or territory that issued the ID</HelpIcon>
        </>
      ),
    },
    {
      value: 'platform',
      label: (
        <>
          Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
        </>
      ),
    },
    {
      value: 'country_select',
      label: (
        <>
          Selected country <HelpIcon>The country chosen at the start of the flow</HelpIcon>
        </>
      ),
    },
  ],
  [ApiModel.VerificationSelfie]: [
    {
      value: 'capture_method',
      label: (
        <>
          Capture method <HelpIcon>The method used to submit the selfie</HelpIcon>
        </>
      ),
    },
    {
      value: 'device_group',
      label: (
        <>
          Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
        </>
      ),
    },
    {
      value: 'platform',
      label: (
        <>
          Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
        </>
      ),
    },
  ],
  [ApiModel.VerificationDatabase]: [
    {
      value: 'country_code',
      label: (
        <>
          Country code{' '}
          <HelpIcon>The country provided by the user to run this verification</HelpIcon>
        </>
      ),
    },
    {
      value: 'device_group',
      label: (
        <>
          Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
        </>
      ),
    },
    {
      value: 'platform',
      label: (
        <>
          Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
        </>
      ),
    },
  ],
  [ApiModel.VerificationAamva]: [
    {
      value: 'device_group',
      label: (
        <>
          Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
        </>
      ),
    },

    {
      value: 'issuing_authority',
      label: (
        <>
          Issuing authority <HelpIcon>The state that issued the Motor Vehicle document</HelpIcon>
        </>
      ),
    },
    {
      value: 'platform',
      label: (
        <>
          Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
        </>
      ),
    },
  ],
};

export class FilterRule {
  dimension?: FilterDimensions;
  operator?: string;
  values?: string[];
}

interface BaseVerificationFilterProps {
  andRule: FilterRule;
  orIndex: number;
  andIndex: number;
  deleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  updateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  verificationType: FilteredVerificationType;
  handleOpenDropdown: () => void;
  handleCloseDropdown: () => void;
  typeDynamicOptions?: DynamicFilterSet;
}

const BaseVerificationFilterRow: VFC<BaseVerificationFilterProps> = ({
  andRule,
  orIndex,
  andIndex,
  updateAndRule,
  deleteAndRule,
  verificationType,
  handleOpenDropdown,
  handleCloseDropdown,
  typeDynamicOptions,
}) => {
  const displayRightHand = andRule.dimension != null;
  const disableRightHand = andRule.operator == null;

  // When the filters are enabled, take the dynamic options if they exist. Otherwise take the hardcoded values
  const optionValues =
    andRule.dimension && typeDynamicOptions != null
      ? typeDynamicOptions[andRule.dimension] || VERIFICATION_VALUES_OPTIONS[andRule.dimension]
      : [];
  return (
    <div data-test='base-filter-row-v2__container'>
      <Grid>
        <InnerGrid>
          <StyledLabel size='sm'>
            {andIndex === 0 ? (orIndex === 0 ? 'Where' : 'Or') : 'And'}
          </StyledLabel>
        </InnerGrid>
      </Grid>
      <Spacer height={0.5} />
      <Grid data-test='base-conditional-inputs'>
        <StyledSelect
          id='base-conditional-input-dimension__select'
          data-test='base-conditional-input-dimension__select'
          aria-label='Select Dimension'
          value={andRule.dimension}
          placeholder='Dimension'
          onChange={(dimension: FilterDimensions) => {
            updateAndRule(verificationType, orIndex, andIndex, dimension, undefined, undefined);
          }}
          options={FIELD_OPTIONS[verificationType as FilteredVerificationType]}
          menuPlacement='bottom'
          size='sm'
          isMulti={false}
          onMenuClose={handleCloseDropdown}
          onMenuOpen={handleOpenDropdown}
          styles={singleSelectStyles}
          portal
        />
        <StyledSelect
          id='base-conditional-input-operator__select'
          aria-label='Select operator'
          value={andRule.operator}
          placeholder='Operator'
          onChange={(operator: string) => {
            updateAndRule(verificationType, orIndex, andIndex, undefined, operator, undefined);
          }}
          onMenuClose={handleCloseDropdown}
          onMenuOpen={handleOpenDropdown}
          options={OPERATOR_TYPES}
          menuPlacement='bottom'
          size='sm'
          styles={singleSelectStyles}
          portal
        />
        {displayRightHand && (
          <MultiSelect
            value={andRule.values || []}
            selectOptionValues={optionValues}
            labelTextMapper={(values) => {
              if (values.length === 1) {
                return optionValues.find((v) => v.value === values[0])?.label;
              } else if (values.length === optionValues.length) {
                return 'All Values';
              } else if (values.length > 1) {
                return `${values.length} Values`;
              } else {
                return 'All Values';
              }
            }}
            onChange={(val) => {
              updateAndRule(verificationType, orIndex, andIndex, undefined, undefined, val);
            }}
            onCloseOptions={handleCloseDropdown}
            onOpenOptions={handleOpenDropdown}
            disabled={disableRightHand}
            size='md'
            selectAllEnabled
            searchEnabled
          />
        )}
        <CenteredIcon>
          <Button
            id='multi-input__value-remove'
            variant='basic'
            width='icon'
            onClick={() => deleteAndRule(verificationType, orIndex, andIndex)}
          >
            <TrashIcon />
          </Button>
        </CenteredIcon>
      </Grid>
    </div>
  );
};

export default BaseVerificationFilterRow;
